export interface RepositoryScopeGuard {
  capture(): number;
  isCurrent(snapshot: number): boolean;
  invalidate(): void;
}

export interface WorkspaceScopeGuard {
  capture(workspaceId: string): number;
  isCurrent(workspaceId: string, snapshot: number): boolean;
  invalidate(workspaceId: string): void;
  clear(): void;
}

export interface WorkspaceLoadTokens {
  bump(workspaceId: string): number;
  isLatest(workspaceId: string, token: number): boolean;
  delete(workspaceId: string): void;
  clear(): void;
}

export function createRepositoryScopeGuard(): RepositoryScopeGuard {
  let generation = 0;

  return {
    capture: (): number => generation,
    isCurrent: (snapshot: number): boolean => snapshot === generation,
    invalidate: (): void => {
      generation += 1;
    },
  };
}

export function createWorkspaceScopeGuard(): WorkspaceScopeGuard {
  const generations = new Map<string, number>();

  return {
    capture: (workspaceId: string): number => generations.get(workspaceId) ?? 0,
    isCurrent: (workspaceId: string, snapshot: number): boolean =>
      (generations.get(workspaceId) ?? 0) === snapshot,
    invalidate: (workspaceId: string): void => {
      generations.set(workspaceId, (generations.get(workspaceId) ?? 0) + 1);
    },
    clear: (): void => {
      generations.clear();
    },
  };
}

export function createWorkspaceLoadTokens(): WorkspaceLoadTokens {
  const tokens = new Map<string, number>();

  return {
    bump: (workspaceId: string): number => {
      const nextToken = (tokens.get(workspaceId) ?? 0) + 1;
      tokens.set(workspaceId, nextToken);
      return nextToken;
    },
    isLatest: (workspaceId: string, token: number): boolean =>
      tokens.get(workspaceId) === token,
    delete: (workspaceId: string): void => {
      tokens.delete(workspaceId);
    },
    clear: (): void => {
      tokens.clear();
    },
  };
}
